<script setup lang="ts">
import { useFlag } from '@unleash/proxy-client-vue'
import { type Component, computed, type ComputedRef } from 'vue'

import { config } from '@/lib/helpers'
import { PAGE_NAME_MAPPING } from '@/lib/helpers/navigation.helpers'
import { FLAG_MARKETING_NOTIFICATIONS } from '@/lib/utils/flags'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

import AdvertisersIcon from '@/assets/icons/advertisers.svg?component'
import CampaignsIcon from '@/assets/icons/campaigns.svg?component'
import ClientsIcon from '@/assets/icons/clients.svg?component'
import CreatorsIcon from '@/assets/icons/creators.svg?component'
import CreatorsMediaIcon from '@/assets/icons/creators-media.svg?component'
import DashboardIcon from '@/assets/icons/dashboard.svg?component'
import DocumentationIcon from '@/assets/icons/documentation.svg?component'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?component'
import LogoutIcon from '@/assets/icons/logout.svg?component'
import PaymentsIcon from '@/assets/icons/payments.svg?component'
import PowerBiIcon from '@/assets/icons/power-bi.svg?component'
import PushIcon from '@/assets/icons/push.svg?component'
import RedirectPostsIcon from '@/assets/icons/redirect-posts.svg?component'
import ReportsIcon from '@/assets/icons/reports.svg?component'
import SegmentsIcon from '@/assets/icons/segments.svg?component'

type MenuItem = {
  title: string
  route: string
  icon: Component
  isExternal?: boolean
  isNewWindow?: boolean
  isRedirect?: boolean
}

const isMarketingNotificationsEnabled = useFlag(FLAG_MARKETING_NOTIFICATIONS)

const menuItems: ComputedRef<MenuItem[]> = computed(() => [
  {
    title: PAGE_NAME_MAPPING.dashboard,
    route: '/',
    icon: DashboardIcon,
  },
  {
    title: PAGE_NAME_MAPPING.advertisers,
    route: '/stores',
    icon: AdvertisersIcon,
  },
  {
    title: PAGE_NAME_MAPPING.creators,
    route: `${config.adminV2Url}/creators`,
    icon: CreatorsIcon,
    isExternal: true,
  },
  {
    title: PAGE_NAME_MAPPING.segments,
    route: `${config.adminV2Url}/segments`,
    icon: SegmentsIcon,
    isExternal: true,
  },
  {
    title: PAGE_NAME_MAPPING.clients,
    route: `${config.adminV2Url}/clients`,
    icon: ClientsIcon,
    isExternal: true,
  },
  {
    title: PAGE_NAME_MAPPING.campaigns,
    route: `${config.adminV2Url}/campaigns`,
    icon: CampaignsIcon,
    isExternal: true,
  },
  {
    title: PAGE_NAME_MAPPING.powerBi,
    route: config.powerBiUrl,
    icon: PowerBiIcon,
    isExternal: true,
    isNewWindow: true,
  },
  {
    title: PAGE_NAME_MAPPING.contentLibrary,
    route: `${config.adminV2Url}/content-library`,
    icon: CreatorsMediaIcon,
    isExternal: true,
  },
  {
    title: PAGE_NAME_MAPPING.payments,
    route: `/payments`,
    icon: PaymentsIcon,
  },
  {
    title: PAGE_NAME_MAPPING.returnAds,
    route: `${config.adminV2Url}/return-ads`,
    icon: RedirectPostsIcon,
    isExternal: true,
  },
  ...(isMarketingNotificationsEnabled.value
    ? [
        {
          title: PAGE_NAME_MAPPING.marketingNotifications,
          route: `${config.adminV2Url}/marketing-notifications`,
          icon: PushIcon,
          isExternal: true,
        },
      ]
    : []),
  {
    title: PAGE_NAME_MAPPING.reports,
    route: `/reports`,
    icon: ReportsIcon,
  },
  {
    title: PAGE_NAME_MAPPING.documentation,
    route: 'https://metapic.getoutline.com',
    icon: DocumentationIcon,
    isExternal: true,
    isNewWindow: true,
  },
])
</script>
<template>
  <flex-component direction="column" class="sidebar-container">
    <flex-component class="sidebar-container__i" direction="column" justify="space-between">
      <box-component :pl="8" :pr="8" :pt="16">
        <flex-component direction="column" grow="1">
          <template v-for="item in menuItems" :key="item.route">
            <a v-if="item.isExternal" :href="item.route" class="sidebar__link" :target="item.isNewWindow ? '_blank' : undefined">
              <box-component :p="20">
                <flex-component align="center" justify="space-between">
                  <flex-component :gap="12" align="center">
                    <el-icon :size="24">
                      <component :is="item.icon" />
                    </el-icon>
                    <typography-component variant="title-medium" :font-weight="500" color="#fff" class="sidebar__link__title">{{
                      item.title
                    }}</typography-component>
                  </flex-component>
                  <external-link-icon v-if="item.isNewWindow" class="sidebar__link__external-icon" />
                </flex-component>
              </box-component>
            </a>
            <router-link v-else :to="item.route" class="sidebar__link" active-class="active">
              <box-component :p="20">
                <flex-component :gap="12" align="center">
                  <el-icon :size="24">
                    <component :is="item.icon" />
                  </el-icon>
                  <typography-component variant="title-medium" :font-weight="500" color="#fff" class="sidebar__link__title">{{
                    item.title
                  }}</typography-component>
                </flex-component>
              </box-component>
            </router-link>
          </template>
        </flex-component>
      </box-component>

      <box-component :p="8">
        <router-link to="/logout">
          <box-component :p="20">
            <flex-component :gap="12" align="center">
              <el-icon :size="24">
                <logout-icon />
              </el-icon>
              <typography-component variant="title-medium" color="#fff" class="sidebar__link__title">
                {{ PAGE_NAME_MAPPING.logout }}
              </typography-component>
            </flex-component>
          </box-component>
        </router-link>
      </box-component>
    </flex-component>
  </flex-component>
</template>
<style scoped>
.sidebar-container {
  position: fixed;
  left: 0;
  top: 72px;
  z-index: 999;

  width: 80px;
  height: calc(100vh - 72px);
  margin-top: 0;

  box-shadow: 2px 0 16px 0 rgba(34, 34, 34, 0.08);
  transition: width ease 0.2s;

  &:hover {
    width: 260px;

    .sidebar__link__title {
      display: initial;
    }
  }

  &:before {
    content: '';
    position: absolute;
    height: 48px;
    width: 24px;
    top: 0;
    right: -24px;

    border-top-left-radius: 24px;
    background-color: transparent;
    box-shadow: 0 -24px 0 0 #222;
  }
}

.sidebar-container__i {
  flex: 1;
  overflow: auto;
  background-color: #222;
}

.sidebar__link {
  display: block;
  position: relative;
  margin-top: 0;
  border-radius: 8px;
  white-space: nowrap;
  text-decoration: none;
  transition: background 0.23s ease;

  &.active,
  &:hover {
    background: #ffffff29;
  }

  &.active {
    &:before {
      content: '';
      position: absolute;
      left: -8px;
      width: 4px;
      height: 64px;
      border-radius: 4px;
      background-color: #fff;
    }
  }
}

.sidebar__link__title {
  display: none;
  margin: 0;
}

.sidebar__link__external-icon {
  opacity: 0.32;
  margin-top: 0;
}
</style>
